import { LexicalEditorWrapper } from 'components/richText'
import { DEFAULT_SEARCHABLE_SLASH_TYPES } from 'features/richText/constants'
import {
  DragDropPastePlugin,
  ImagePlugin,
  MentionPlugin,
  SlashCommandPlugin,
} from 'features/richText/editorPlugins'
import {
  ImageNode,
  MentionNode,
  SlashCommandNode,
} from 'features/richText/nodes'
import React from 'react'
import {
  EditorMode,
  RichTextConfig,
  RichTextContent,
  RolodexEntryType,
  sanitizeHtmlContent,
} from 'types'

import { RichTextViewer } from './RichTextViewer'
import { ToolbarPlugin } from './ToolbarPlugin'

/**
 * Props for the RichTextEditorLexical component.
 *
 * @property value - The current rich text content value.
 * @property onChange - Callback invoked when the content changes.
 * @property className - Optional CSS class for the editor container.
 * @property enableMentions - Whether to show mention (@) functionality.
 * @property enableSlashCommands - Whether to show slash command (/) functionality.
 * @property entryIdForImages - The Rolodex entry ID to associate uploaded images with. When provided, enables image upload functionality.
 * @property maxMentionSuggestions - Maximum number of mention suggestions.
 * @property maxSlashSuggestions - Maximum number of slash command suggestions.
 * @property searchableSlashTypes - Entry types that can be searched via slash commands.
 * @property emptyMessage - Message to show when content is empty (view mode only).
 * @property interactive - Whether to allow interaction with embedded objects (view mode only).
 * @property showToolbar - Whether to show the formatting toolbar (edit mode only).
 */
export interface RichTextEditorLexicalProps extends RichTextConfig {
  value?: RichTextContent
  onChange?: (content: RichTextContent) => void
  className?: string
  enableMentions?: boolean
  enableSlashCommands?: boolean
  entryIdForImages?: string
  maxMentionSuggestions?: number
  maxSlashSuggestions?: number
  searchableSlashTypes?: RolodexEntryType[]
  emptyMessage?: string
  showToolbar?: boolean
  interactive?: boolean
}

export const RichTextEditorLexical: React.FC<RichTextEditorLexicalProps> = ({
  value = '' as RichTextContent,
  onChange,
  placeholder = 'Start typing...',
  readOnly = false,
  mode = EditorMode.EDIT,
  autoFocus = false,
  className,
  enableMentions = true,
  enableSlashCommands = true,
  entryIdForImages,
  maxMentionSuggestions = 10,
  maxSlashSuggestions = 10,
  searchableSlashTypes = DEFAULT_SEARCHABLE_SLASH_TYPES,
  emptyMessage = 'No content',
  showToolbar = false,
  interactive = true,
}) => {
  if (readOnly || mode === EditorMode.VIEW) {
    return (
      <RichTextViewer
        content={value}
        className={className}
        emptyMessage={emptyMessage}
        interactive={interactive}
      />
    )
  }

  const customNodes = [MentionNode, SlashCommandNode, ImageNode]

  const handleSanitizedChange = (content: RichTextContent) => {
    if (onChange) {
      const sanitizedContent = sanitizeHtmlContent(content)
      onChange(sanitizedContent)
    }
  }

  return (
    <LexicalEditorWrapper
      value={value}
      onChange={handleSanitizedChange}
      placeholder={placeholder}
      readOnly={readOnly}
      autoFocus={autoFocus}
      className={className}
      nodes={customNodes}
      richText={true}
      minHeight='8rem'
      toolbar={
        showToolbar ? (
          <ToolbarPlugin entryIdForImages={entryIdForImages} />
        ) : undefined
      }>
      {enableMentions && (
        <MentionPlugin maxSuggestions={maxMentionSuggestions} />
      )}
      {enableSlashCommands && (
        <SlashCommandPlugin
          maxSuggestions={maxSlashSuggestions}
          searchableTypes={searchableSlashTypes}
        />
      )}
      {entryIdForImages && (
        <>
          <ImagePlugin />
          <DragDropPastePlugin entryIdForImages={entryIdForImages} />
        </>
      )}
    </LexicalEditorWrapper>
  )
}
